import { enumToOptions } from "@repo/helpers";
import { DateTimePicker, DropDownList, FiltersV2, Loading, TextBox } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import type { ComplaintsFilter } from "../../models/complaint";

import ComplaintsApi from "../../api/complaintsApi";
import { QUERY_KEYS } from "../../api/queries";
import { SegmentFilters } from "../../components/segmentFilters";
import { yesNoOptions } from "../../constants/constants";
import { CommodityTypeEnum, RepIncidentAssignedStatus, RepIncidentStatus } from "../../models/complaint";
import { CustomerIncidentsGrid } from "./grid";
import {
	AGENT_ID,
	ASSIGNED_STATUS,
	COMMISSION_COMPLAINT,
	COMMODITY,
	COMPLAINT_DATE_AFTER,
	COMPLAINT_DATE_BEFORE,
	COMPLAINT_ID,
	COMPLAINT_OR_INCIDENT,
	CREATED_ON_FROM,
	CREATED_ON_TO,
	CUSTOMER_ACCOUNT_NUMBER,
	CUSTOMER_NAME,
	CUSTOMER_PHONE,
	ENROLLMENT_DATE_AFTER,
	ENROLLMENT_DATE_BEFORE,
	ESI_ID,
	OPE,
	QA_ANALYST_ID,
	REPORTING_TAG_ID,
	REPRESENTATIVE_FIRST_NAME,
	REPRESENTATIVE_LAST_NAME,
	SOURCE_ID,
	STATUS,
	TYPE_ID,
} from "./strings";

const initialFilters: ComplaintsFilter = {
	currentPage: 1,
	itemsPerPage: 10,
	complaintDateAfter: null,
	complaintDateBefore: null,
	createdOnFrom: null,
	createdOnTo: null,
	enrollmentDateAfter: null,
	enrollmentDateBefore: null,
	customerAccountNumber: "",
	complaintType: "",
	repId: "",
	firstName: "",
	lastName: "",
	representativeFirstName: "",
	representativeLastName: "",
	esiId: "",
	agentId: "",
	customerName: "",
	customerPhone: "",
	complaintId: "",
	status: "",
	assignedStatus: "",
	qaAnalystId: null,
	sourceId: null,
	reportingTagId: null,
	complaintOrIncident: null,
	commissionComplaint: "",
	commodity: "",
	ope: "",
	typeId: [],
	branchIds: [],
};

export function CustomerIncidentsPage() {
	const [draftFilters, setDraftFilters] = useState<ComplaintsFilter>(initialFilters);
	const [appliedFilters, setAppliedFilters] = useState<ComplaintsFilter>(initialFilters);

	// Data queries for dropdown options
	const complaintTypesQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.ComplaintTypes],
		queryFn: () => ComplaintsApi.getComplaintTypes(),
	});

	const complaintSourcesQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.ComplaintSources],
		queryFn: () => ComplaintsApi.getComplaintSources(),
	});

	const usersQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.Users],
		queryFn: () => ComplaintsApi.getUsers(),
	});

	const reportingTagsQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.ReportingTags, draftFilters.branchIds],
		queryFn: () => ComplaintsApi.getReportingTags(draftFilters.branchIds),
	});

	const complaintOrIncidentQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.ComplaintOrIncident],
		queryFn: () => ComplaintsApi.getComplaintOrIncident(),
	});

	const complaintSettingsQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.ComplaintSettings],
		queryFn: () => ComplaintsApi.settings(),
	});

	const filterChanged = (name: string, value: any) => {
		setDraftFilters((prev) => ({ ...prev, [name]: value }));
	};

	const segmentFiltersChanged = useCallback(
		(s) =>
			setDraftFilters((prev) => ({
				...prev,
				brands: s.brandIds,
				channels: s.channelIds,
				marketers: s.marketerIds,
				states: s.stateIds,
				branches: s.branchIds,
			})),
		[setDraftFilters],
	);

	return (
		<Loading loading={complaintSettingsQuery.isLoading}>
			<main>
				<FiltersV2
					onApply={() =>
						setAppliedFilters((currentFilters) => ({
							...draftFilters,
							currentPage: 1,
							itemsPerPage: currentFilters.itemsPerPage,
						}))
					}
				>
					{/* Date Filters */}
					<DateTimePicker
						id={COMPLAINT_DATE_AFTER}
						name={COMPLAINT_DATE_AFTER}
						label="Date of Incident after"
						value={draftFilters.complaintDateAfter}
						changeHandler={(v) => filterChanged(COMPLAINT_DATE_AFTER, v)}
					/>
					<DateTimePicker
						id={COMPLAINT_DATE_BEFORE}
						name={COMPLAINT_DATE_BEFORE}
						label="Date of Incident before"
						value={draftFilters.complaintDateBefore}
						changeHandler={(v) => filterChanged(COMPLAINT_DATE_BEFORE, v)}
					/>
					<DateTimePicker
						id={CREATED_ON_FROM}
						name={CREATED_ON_FROM}
						label="Created On From"
						value={draftFilters.createdOnFrom}
						changeHandler={(v) => filterChanged(CREATED_ON_FROM, v)}
					/>
					<DateTimePicker
						id={CREATED_ON_TO}
						name={CREATED_ON_TO}
						label="Created On To"
						value={draftFilters.createdOnTo}
						changeHandler={(v) => filterChanged(CREATED_ON_TO, v)}
					/>
					<SegmentFilters onChange={segmentFiltersChanged} />

					<TextBox
						id={REPRESENTATIVE_FIRST_NAME}
						name={REPRESENTATIVE_FIRST_NAME}
						label="Representative First Name"
						value={draftFilters.representativeFirstName}
						changeHandler={(v) => filterChanged(REPRESENTATIVE_FIRST_NAME, v)}
					/>
					<TextBox
						id={REPRESENTATIVE_LAST_NAME}
						name={REPRESENTATIVE_LAST_NAME}
						label="Representative Last Name"
						value={draftFilters.representativeLastName}
						changeHandler={(v) => filterChanged(REPRESENTATIVE_LAST_NAME, v)}
					/>
					<TextBox
						id={ESI_ID}
						name={ESI_ID}
						label="ESId"
						value={draftFilters.esiId}
						changeHandler={(v) => filterChanged(ESI_ID, v)}
					/>
					<TextBox
						id={CUSTOMER_ACCOUNT_NUMBER}
						name={CUSTOMER_ACCOUNT_NUMBER}
						label="Customer Account Number"
						value={draftFilters.customerAccountNumber}
						changeHandler={(v) => filterChanged(CUSTOMER_ACCOUNT_NUMBER, v)}
					/>
					<DropDownList
						id={STATUS}
						name={STATUS}
						label="Status"
						value={draftFilters.status}
						changeHandler={(v) => filterChanged(STATUS, v)}
						fullLength
						clearable
						values={enumToOptions(RepIncidentStatus)}
					/>
					<DropDownList
						id={ASSIGNED_STATUS}
						name={ASSIGNED_STATUS}
						label="Assigned Status"
						value={draftFilters.assignedStatus}
						changeHandler={(v) => filterChanged(ASSIGNED_STATUS, v)}
						fullLength
						clearable
						values={enumToOptions(RepIncidentAssignedStatus)}
					/>
					<DropDownList
						id={QA_ANALYST_ID}
						name={QA_ANALYST_ID}
						label="QA Analyst"
						value={draftFilters.qaAnalystId?.toString() || ""}
						changeHandler={(v) => filterChanged(QA_ANALYST_ID, v ? parseInt(v) : null)}
						fullLength
						clearable
						loaded={usersQuery.isFetched}
						values={usersQuery.data ?? []}
						valueProp="Id"
						textProp="Name"
					/>
					<DropDownList
						id={TYPE_ID}
						name={TYPE_ID}
						label="Type"
						value={draftFilters.complaintType?.toString() || ""}
						changeHandler={(v) => filterChanged(TYPE_ID, v ? parseInt(v) : null)}
						fullLength
						clearable
						loaded={complaintTypesQuery.isFetched}
						values={complaintTypesQuery.data ?? []}
						textProp="Description"
						valueProp="Id"
					/>
					<DropDownList
						id={SOURCE_ID}
						name={SOURCE_ID}
						label="Source"
						value={draftFilters.sourceId?.toString() || ""}
						changeHandler={(v) => filterChanged(SOURCE_ID, v ? parseInt(v) : null)}
						fullLength
						clearable
						loaded={complaintSourcesQuery.isFetched}
						values={complaintSourcesQuery.data ?? []}
						textProp="Description"
						valueProp="Id"
					/>
					<DropDownList
						id={REPORTING_TAG_ID}
						name={REPORTING_TAG_ID}
						label="Reporting Tag"
						value={draftFilters.reportingTagId?.toString() || ""}
						changeHandler={(v) => filterChanged(REPORTING_TAG_ID, v ? parseInt(v) : null)}
						fullLength
						clearable
						loaded={reportingTagsQuery.isFetched}
						values={reportingTagsQuery.data ?? []}
						textProp="Name"
						valueProp="Id"
					/>
					<TextBox
						id={AGENT_ID}
						name={AGENT_ID}
						label="Rep Id"
						value={draftFilters.agentId}
						changeHandler={(v) => filterChanged(AGENT_ID, v)}
					/>
					<TextBox
						id={CUSTOMER_NAME}
						name={CUSTOMER_NAME}
						label="Customer Name"
						value={draftFilters.customerName}
						changeHandler={(v) => filterChanged(CUSTOMER_NAME, v)}
					/>
					<DateTimePicker
						id={ENROLLMENT_DATE_AFTER}
						name={ENROLLMENT_DATE_AFTER}
						label="Date of Enrollment after"
						value={draftFilters.enrollmentDateAfter}
						changeHandler={(v) => filterChanged(ENROLLMENT_DATE_AFTER, v)}
					/>
					<DateTimePicker
						id={ENROLLMENT_DATE_BEFORE}
						name={ENROLLMENT_DATE_BEFORE}
						label="Date of Enrollment before"
						value={draftFilters.enrollmentDateBefore}
						changeHandler={(v) => filterChanged(ENROLLMENT_DATE_BEFORE, v)}
					/>
					<TextBox
						id={CUSTOMER_PHONE}
						name={CUSTOMER_PHONE}
						label="Customer Phone"
						value={draftFilters.customerPhone}
						changeHandler={(v) => filterChanged(CUSTOMER_PHONE, v)}
					/>
					<DropDownList
						id={COMPLAINT_OR_INCIDENT}
						name={COMPLAINT_OR_INCIDENT}
						label="Incident Category"
						value={draftFilters.complaintOrIncident?.toString() || ""}
						changeHandler={(v) => filterChanged(COMPLAINT_OR_INCIDENT, v ? parseInt(v) : null)}
						fullLength
						clearable
						loaded={complaintOrIncidentQuery.isFetched}
						values={complaintOrIncidentQuery.data ?? []}
						textProp="Name"
						valueProp="Id"
					/>
					{complaintSettingsQuery.data?.showCommissionComplaint && (
						<DropDownList
							id={COMMISSION_COMPLAINT}
							name={COMMISSION_COMPLAINT}
							label="ZPV"
							value={draftFilters.commissionComplaint}
							changeHandler={(v) => filterChanged(COMMISSION_COMPLAINT, v)}
							fullLength
							clearable
							values={yesNoOptions}
						/>
					)}
					<DropDownList
						id={COMMODITY}
						name={COMMODITY}
						label="Commodity"
						value={draftFilters.commodity}
						changeHandler={(v) => filterChanged(COMMODITY, v)}
						fullLength
						clearable
						values={enumToOptions(CommodityTypeEnum)}
					/>
					<DropDownList
						id={OPE}
						name={OPE}
						label="Office of the President/Executive"
						value={draftFilters.ope}
						changeHandler={(v) => filterChanged(OPE, v)}
						fullLength
						clearable
						values={yesNoOptions}
					/>
					<TextBox
						id={COMPLAINT_ID}
						name={COMPLAINT_ID}
						label="Complaint ID"
						value={draftFilters.complaintId}
						changeHandler={(v) => filterChanged(COMPLAINT_ID, v)}
					/>
				</FiltersV2>
				<CustomerIncidentsGrid
					filters={appliedFilters}
					setFilters={setAppliedFilters}
					showCommissionComplaint={complaintSettingsQuery.data?.showCommissionComplaint ?? false}
				/>
			</main>
		</Loading>
	);
}
